/**
 * Calculate Ideal Body Weight (IBW) using the Devine formula.
 * Male: 50 + 2.3 * (heightInches - 60)
 * Female: 45.5 + 2.3 * (heightInches - 60)
 */
export function calculateIBW(gender, heightInches) {
  if (!heightInches || heightInches <= 0) return null;
  const base = gender === 'M' ? 50 : 45.5;
  const ibw = base + 2.3 * (heightInches - 60);
  return Math.round(ibw * 10) / 10;
}

/**
 * Calculate tidal volume range for a given weight (IBW or ABW).
 * Returns object with keys 4-8 (cc/kg) and values in mL.
 */
export function calculateTidalVolumeRange(weightKg) {
  if (!weightKg || weightKg <= 0) return null;
  const result = {};
  for (let ccPerKg = 4; ccPerKg <= 8; ccPerKg++) {
    result[ccPerKg] = Math.round(weightKg * ccPerKg);
  }
  return result;
}

/**
 * Calculate dose from rate.
 * dose = rate (mL/hr) * concentration (units/mL)
 * Handles unit conversions: mg <-> mcg (factor 1000)
 */
export function calculateDoseFromRate(rateMlHr, concentration, concentrationUnit, doseUnit, patientWeightKg) {
  if (!rateMlHr || !concentration) return null;

  // Normalize concentration to base unit (same as dose numerator)
  let concInBaseUnit = concentration;
  const concUnit = concentrationUnit?.split('/')[0]?.toLowerCase() || '';
  const doseBase = doseUnit?.split('/')[0]?.toLowerCase() || '';

  // Convert concentration unit to match dose unit
  if (concUnit === 'mg' && doseBase === 'mcg') {
    concInBaseUnit = concentration * 1000;
  } else if (concUnit === 'mcg' && doseBase === 'mg') {
    concInBaseUnit = concentration / 1000;
  }

  let dose = rateMlHr * concInBaseUnit;

  // If dose unit is weight-based (/kg/hr), divide by patient weight
  if (doseUnit?.includes('/kg') && patientWeightKg) {
    dose = dose / patientWeightKg;
  }

  return Math.round(dose * 1000) / 1000;
}

/**
 * Calculate rate from dose.
 * rate (mL/hr) = dose / concentration
 */
export function calculateRateFromDose(dose, concentration, concentrationUnit, doseUnit, patientWeightKg) {
  if (!dose || !concentration) return null;

  let effectiveDose = dose;

  // If dose is weight-based, multiply by weight first to get total dose/hr
  if (doseUnit?.includes('/kg') && patientWeightKg) {
    effectiveDose = dose * patientWeightKg;
  }

  // Normalize units
  let concInBaseUnit = concentration;
  const concUnit = concentrationUnit?.split('/')[0]?.toLowerCase() || '';
  const doseBase = doseUnit?.split('/')[0]?.toLowerCase() || '';

  if (concUnit === 'mg' && doseBase === 'mcg') {
    concInBaseUnit = concentration * 1000;
  } else if (concUnit === 'mcg' && doseBase === 'mg') {
    concInBaseUnit = concentration / 1000;
  }

  if (concInBaseUnit === 0) return null;
  const rate = effectiveDose / concInBaseUnit;
  return Math.round(rate * 1000) / 1000;
}

/**
 * Calculate time remaining in minutes.
 * timeRemaining = vtbi (mL) / rate (mL/hr) * 60
 */
export function calculateTimeRemaining(vtbiMl, rateMlHr) {
  if (!vtbiMl || !rateMlHr || rateMlHr <= 0) return null;
  return Math.round((vtbiMl / rateMlHr) * 60);
}

/**
 * Format minutes as HH:MM string.
 */
export function formatMinutesAsHHMM(minutes) {
  if (minutes == null || minutes < 0) return '--:--';
  const hrs = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Convert between weight units.
 */
export function lbsToKg(lbs) {
  return Math.round(lbs * 0.453592 * 10) / 10;
}

export function kgToLbs(kg) {
  return Math.round(kg * 2.20462 * 10) / 10;
}

/**
 * Convert height: feet+inches to total inches.
 */
export function feetInchesToInches(feet, inches) {
  return (feet * 12) + (inches || 0);
}

export function inchesToFeetInches(totalInches) {
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return { feet, inches };
}
